import React, { useState, useEffect, useContext } from "react";
import classes from "./Trucks.module.css";
import { Row } from "../Shared/FlexDiv/FlexDiv";
import { Activity, currentRequestTimeStamp, isMobile } from "../../Constants";
import ReactGA from "react-ga";
import { Urls } from "../../Urls";
import mixpanel from "mixpanel-browser";
import RouterOutlet from "./TrucksRoute";
import { Link } from "react-router-dom";
import { useMixPanelForTracking } from "../../utils/TrackMixPanel";
import { UserApiContext } from "../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../MyCompanyProfileContext";
import useViewOnlyAccess from "../../hooks/useViewOnlyAccess";
import ModuleHeading from "../Shared/ModuleHeading/ModuleHeading";
import Unauthorized from "../Unauthorized";
import { Redirect } from "react-router-dom";


/**
 * TrucksNew component manages the display of different sections (Dashboard, My Enquiries, Transporter Enquiries)
 * within the Trucks module. It also integrates with Google Analytics and Mixpanel for tracking user interactions.
 *
 * @component
 * @param {Object} props - The component props.
 * @returns {React.ReactElement} The TrucksNew component, including navigation and routing.
 */
const TrucksNew = (props) => {
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showTransporter, setShowTransporter] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);
  const { isRestricted, hasModuleAccess, dashboardAccess, noAccess, loading } = useViewOnlyAccess('spot');
  const [userContent, setuserContent] = useState(<></>);

  const isDashboardAllowedByPolicy =
    userDetails?.policy?.adhoc?.allowed_dashboard === "yes";

  /**
   * useEffect hook to track page views and set the appropriate state based on the current URL.
   * Integrates Google Analytics and Mixpanel for tracking.
   *
   * @function
   */
  useEffect(() => {
    ReactGA.pageview("Trucks Available");
    Activity([
      {
        screen: "LoRRI Adhoc",
        module: "Adhoc Dashboard",
        user_activity: "User has viewed Adhoc Dashboard",
        timestamp: currentRequestTimeStamp(),
      },
    ]);
    // TrackMixPanel({
    //   ModuleName: "lorri spot",
    //   ScreenName: "my enquiries",
    //   Data: `None`,
    //   Definition: "user has viewed my enquiries section",
    // });

    if (window.location.pathname === "/trucks/dashboard") {
      setShowDashboard(true);
      setShowEnquiry(false);
      setShowTransporter(false);
    } else if (window.location.pathname.startsWith("/trucks/enquiries")) {
      setShowEnquiry(true);
      setShowDashboard(false);
      setShowTransporter(false);
    } else if (window.location.pathname === "/trucks/transporterenquiries") {
      setShowTransporter(true);
      setShowDashboard(false);
      setShowEnquiry(false);
      setShowDrafts(false);
    } else if (window.location.pathname === "/trucks/drafts") {
      setShowDrafts(true);
      setShowDashboard(false);
      setShowEnquiry(false);
      setShowTransporter(false);
    }
  }, [showEnquiry, showDashboard, showTransporter]);

  // Handle unauthorized access
  useEffect(() => {
    if (!loading && hasModuleAccess === false) {
      setuserContent(<Unauthorized
        message="You dont have access for this module"
        showPremiumButton={false}
      />);
    }
  }, [loading, hasModuleAccess]);

  if (!loading && hasModuleAccess === false) {
    return userContent;
  }

  /**
   * Handles the event when the "My Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const newenquiryClicked = (e) => {
    setShowEnquiry(true);
    setShowDashboard(false);
    setShowTransporter(false);
    Activity([
      {
        screen: "Trucks Available",
        module: "Transporter Enquiries",
        user_activity: "Transporter Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "transporter enquiries",
      Data: `None`,
      Definition: "user has viewed transporter enquiries section",
    });
  };

  /*
   * Handles the event when the "Transporter Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const trucksClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(false);
    setShowTransporter(true);
    Activity([
      {
        screen: "Trucks Available",
        module: "My Enquiries",
        user_activity: "My Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "my enquiries",
      Data: `None`,
      Definition: "user has viewed my enquiries section",
    });
  };

  /**
   * Handles the event when the "Draft Enquiries" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const draftsClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(false);
    setShowTransporter(false);
    setShowDrafts(true);
    Activity([
      {
        screen: "Trucks Available",
        module: "Draft Enquiries",
        user_activity: "Draft Enquiries section visited",
        timestamp: currentRequestTimeStamp(),
      },
    ]);
  };

  /**
   * Handles the event when the "Dashboard" tab is clicked. Updates the state and tracks the activity.
   *
   * @function
   * @param {Event} e - The click event.
   */
  const dashboardClicked = (e) => {
    setShowEnquiry(false);
    setShowDashboard(true);
    setShowTransporter(false);
    Activity([
      {
        screen: "Trucks Available",
        module: "Adhoc Dashboard",
        user_activity: "Adhoc Dashboard section visited",
        timestamp: currentRequestTimeStamp(),
      },
    ]);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "dashboard",
      Data: `None`,
      Definition: "user has viewed spot dashboard section",
    });
  };

  return (
    <>
      <ModuleHeading title="LoRRI Spot" viewOnly={isRestricted} />

      {/* Main container for Trucks module with navigation and routing */}
      <div className={`${classes["truckscontainer"]}`}>
        {/* Redirect if dashboard is locked and user is on dashboard */}
        {!dashboardAccess && window.location.pathname === "/trucks/dashboard" && (
          <Redirect to="/trucks/enquiries" />
        )}
        {/* Redirect if dashboard not allowed by policy and user is on dashboard */}
        {!isDashboardAllowedByPolicy && window.location.pathname === "/trucks/dashboard" && (
          <Redirect to="/trucks/enquiries" />
        )}
        {/* Redirect if noAccess (dashboard-only) and user is not on dashboard */}
        {hasModuleAccess && noAccess && !window.location.pathname.startsWith("/trucks/dashboard") && (
          <Redirect to="/trucks/dashboard" />
        )}
        <div className="row">
          <ul
            className="nav nav-tabs"
            id="myTab"
            role="tablist"
            style={{ paddingTop: "1%" }}
          >
            {isDashboardAllowedByPolicy && (
            <li className="nav-item">
              <div
                className={`nav-link ${showDashboard === true &&
                  window.location.pathname === "/trucks/dashboard"
                  ? "active"
                  : ""
                  }`}
              >
                <Link
                  to={!dashboardAccess ? "#" : { pathname: "/trucks/dashboard" }}
                  id="adhocdashboard"
                  className={`btn btn-link ${!dashboardAccess ? classes['restricted-link'] : ''}`}
                  onClick={(e) => !dashboardAccess ? e.preventDefault() : dashboardClicked(e)}
                  style={{
                    color: "#393185",
                    fontSize: isMobile() ? "14px" : "17px",
                    fontWeight: "bolder",
                    padding: isMobile() ? "0" : "",
                  }}
                >
                  Dashboard {!dashboardAccess && <i className="fa fa-lock ms-2" />}
                </Link>
              </div>
            </li>
            )}
            <li className="nav-item">
              <div
                className={`nav-link ${showEnquiry === true ||
                  window.location.pathname.startsWith("/trucks/enquiries")
                  ? "active"
                  : ""
                  }`}
              >
                <Link
                  to={!noAccess ? { pathname: "/trucks/enquiries" } : "#"}
                  id="enquiries"
                  className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                  onClick={(e) => !noAccess && newenquiryClicked(e)}
                  style={{
                    color: "#393185",
                    fontSize: isMobile() ? "14px" : "17px",
                    fontWeight: "bolder",
                    padding: isMobile() ? "0" : "",
                  }}
                >
                  My Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                </Link>
              </div>
            </li>
            {/* check if agent settings is enabled and agent mode is set to draft */}
            {
              companySettingData && companySettingData?.agent_settings?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.allowed === "yes" && companySettingData?.agent_settings?.spot_agent?.enabled === "yes" &&
              < li className="nav-item">
                <div
                  className={`nav-link ${showDrafts === true &&
                    window.location.pathname === "/trucks/drafts"
                    ? "active"
                    : ""
                    }`}
                >
                  <Link
                    to={!noAccess ? { pathname: "/trucks/drafts" } : "#"}
                    id="draftenquiries"
                    className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                    onClick={(e) => !noAccess && draftsClicked(e)}
                    style={{
                      color: "#393185",
                      fontSize: isMobile() ? "14px" : "17px",
                      fontWeight: "bolder",
                      padding: isMobile() ? "0" : "",
                    }}
                  >
                    Draft Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                  </Link>
                </div>
              </li>
            }
            <li className="nav-item">
              <div
                className={`nav-link ${showTransporter === true &&
                  window.location.pathname === "/trucks/transporterenquiries"
                  ? "active"
                  : ""
                  }`}
              >
                <Link
                  to={!noAccess ? { pathname: "/trucks/transporterenquiries" } : "#"}
                  id="transporterenquiries"
                  className={`btn btn-link ${noAccess ? classes['restricted-link'] : ''}`}
                  onClick={(e) => !noAccess && trucksClicked(e)}
                  style={{
                    color: "#393185",
                    fontSize: isMobile() ? "14px" : "17px",
                    fontWeight: "bolder",
                    padding: isMobile() ? "0" : "",
                  }}
                >
                  Transporter Enquiries {noAccess && <i className="fa fa-lock ms-2" />}
                </Link>
              </div>
            </li>
          </ul>
        </div>
        <div className="row">
          <div style={{ width: "90%" }}>
            <RouterOutlet />
          </div>
        </div>
      </div >
    </>
  );
};

export default TrucksNew;
